import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";

import AfricaLoudIcon from "./icon-nobg.png";

const BRAND = {
  white: "#ffffff",
  charcoal: "#0b0b0f",
  gray: "#6b7280",
  burgundy: "#800020",
  gold: "#b08d57",
  deepGreen: "#006400",
  teal: "#0f766e",      // season colour
  tealDeep: "#0a4f4a",
  tealLight: "#5eead4",
};

const MODES = {
  home: "home",
  culture: "culture",
  fashion: "fashion",
};

const MODE_TITLES = {
  home: "Africa Loud | Editorial",
  culture: "Culture X Music | Africa Loud",
  fashion: "Art X Fashion | Africa Loud",
};

const CULTURE_MUSIC_ARTICLES = [
  {
    tag: "Essay",
    title: "Layers: The Multi-Dimensional Identity of African Sound",
    blurb:
      "An exploration of how history, migration, and digital speed create the layers of modern African music.",
    meta: "Culture x Music",
    to: "/culture-music/layers-identity-sound",
  },
  {
    tag: "Feature",
    title: "From K-Pop to Afrobeats & Amapiano",
    blurb:
      "Exporting sound without flattening culture and what travel-ready music requires to stay credible.",
    meta: "Culture X Music",
    to: "/culture-music/from-k-pop-to-afrobeats-amapiano",
  },
  {
    tag: "Framework",
    title: "What Are Subcultures in the African Context?",
    blurb:
      "A grounded definition of African subcultures as systems of identity and influence.",
    meta: "Culture X Music",
    to: "/culture-music/african-subcultures",
  },
  {
    tag: "Analysis",
    title: "beyonce, Genres, and the Global Scene",
    blurb:
      "Mapping the intersection of global pop royalty and native African sonic architectures.",
    meta: "Culture X Music",
    to: "/culture-music/beyonce-genres-global-scene",
  },
  {
    tag: "Debate",
    title: "Afrobeat vs. Afrobeats",
    blurb:
      "Unpacking the historical, political, and musical distinctions between two defining eras of sound.",
    meta: "Culture X Music",
    to: "/culture-music/afrobeat-vs-afrobeats",
  },
  {
    tag: "Editorial",
    title: "Town Talk",
    blurb:
      "The latest signals and shifts happening on the ground across the continent's cultural hubs.",
    meta: "Culture X Music",
    to: "/culture-music/town-talk",
  },
  {
    tag: "Profile",
    title: "The Rise of Tyla",
    blurb:
      "A rise story as adoption study and what it reveals about cultural velocity.",
    meta: "Culture X Music",
    to: "/culture-music/the-rise-of-tyla",
  },
  {
    tag: "Essay",
    title: "Language as History",
    blurb:
      "How vernacular and slang in modern music map the historical journey of urban youth.",
    meta: "Culture X Music",
    to: "/culture-music/language-as-history",
  },
  {
    tag: "Commentary",
    title: "Is Tyla Afrobeats?",
    blurb:
      "Deconstructing the industry's rush to categorise and the nuances of South African pop exports.",
    meta: "Culture X Music",
    to: "/culture-music/is-tyla-afrobeats",
  },
  {
    tag: "Deep Dive",
    title: "Amapiano: The Sound of the Youth",
    blurb:
      "Tracing the origins and the unstoppable global trajectory of South Africa's defining genre.",
    meta: "Culture X Music",
    to: "/culture-music/amapiano",
  },
  {
    tag: "Business",
    title: "Invest in Rwanda",
    blurb:
      "Looking at Kigali's emerging positioning as a central node for the African creative economy.",
    meta: "Culture X Music",
    to: "/culture-music/invest-in-rwanda",
  },
  {
    tag: "Culture",
    title: "N|uu: Africa's Oldest Language Still Speaking",
    blurb:
      "A reflection on N|uu, one of Southern Africa's oldest surviving Indigenous languages, and the fight to preserve its memory and cultural inheritance.",
    meta: "Culture",
    to: "/",
  },
];

const ART_FASHION_ARTICLES = [
  {
    tag: "Style",
    title: "African Fashion Renaissance",
    blurb: "Fashion as signal, craft, identity and cultural systems.",
    meta: "Art X Fashion",
    to: "/art-fashion/african-fashion-renaissance",
  },
  {
    tag: "Feature",
    title: "Christopher Emefiele",
    blurb:
      "A portrait of cross-cultural design language and modern African aesthetics.",
    meta: "Art X Fashion",
    to: "/art-fashion/christopher-emefiele-cultural-fusion",
  },
{
  tag: "Campaign",
  title: "Africa in Bloom: The Teal Edit",
  blurb:
    "Before the campaign opens, the colour we're building the season around.",
  meta: "Art X Fashion",
  to: "/art-fashion/africa-in-bloom-teal-edit",
},
];

function Rule({ className = "" }) {
  return <div className={"h-px w-full bg-black/10 " + className} />;
}

function Box({ children, className = "" }) {
  return (
    <div className={"border border-black/15 bg-white " + className}>
      {children}
    </div>
  );
}

function TagPill({ children, tone = "neutral" }) {
  const style =
    tone === "burgundy"
      ? {
          borderColor: BRAND.burgundy + "55",
          backgroundColor: BRAND.burgundy + "08",
        }
      : tone === "gold"
      ? {
          borderColor: BRAND.gold + "55",
          backgroundColor: BRAND.gold + "10",
        }
      : tone === "deepGreen"
      ? {
          borderColor: BRAND.deepGreen + "55",
          backgroundColor: BRAND.deepGreen + "10",
        }
      : tone === "teal"
      ? {
          borderColor: BRAND.teal + "55",
          backgroundColor: BRAND.teal + "10",
        }
      : {};

  return (
    <span
      style={style}
      className="inline-flex items-center border px-2 py-1 text-[11px] uppercase tracking-[0.18em] font-semibold"
    >
      {children}
    </span>
  );
}

function SeasonBanner() {
  const [open, setOpen] = useState(true);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="overflow-hidden"
          style={{ backgroundColor: BRAND.teal }}
        >
          <div className="mx-auto flex max-w-6xl items-center justify-between gap-4 px-4 py-2.5 sm:px-6">
            <Link
              to="/collab"
              className="flex flex-1 flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center text-white"
            >
              <span className="text-[11px] font-bold uppercase tracking-[0.22em]">
                Africa in Bloom
              </span>
              <span className="hidden h-3 w-px bg-white/40 sm:block" />
              <span className="text-[11px] uppercase tracking-[0.14em] text-white/90">
                Our campaign is live &middot; Teal is the colour of the season
              </span>
              <ArrowRight className="h-3 w-3" />
            </Link>

            <button
              onClick={() => setOpen(false)}
              aria-label="Dismiss banner"
              className="text-lg leading-none text-white/70 hover:text-white"
            >
              &times;
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function BloomAd({ compact = false }) {
  return (
    <div className={compact ? "mt-8" : "mt-6"}>
      <div
        className="relative overflow-hidden border"
        style={{
          borderColor: BRAND.tealDeep,
          background: `linear-gradient(135deg, ${BRAND.tealDeep} 0%, ${BRAND.teal} 60%, #14958b 100%)`,
        }}
      >
        {/* Decorative bloom */}
        <svg
          aria-hidden="true"
          viewBox="0 0 200 200"
          className="pointer-events-none absolute -right-10 -top-10 h-72 w-72 opacity-25 sm:h-96 sm:w-96"
        >
          {[0, 45, 90, 135].map((deg) => (
            <ellipse
              key={deg}
              cx="100"
              cy="100"
              rx="18"
              ry="70"
              fill={BRAND.tealLight}
              transform={`rotate(${deg} 100 100)`}
            />
          ))}
          <circle cx="100" cy="100" r="12" fill="#ffffff" />
        </svg>

        <div
          className={
            "relative grid gap-8 " +
            (compact
              ? "p-6 sm:p-8"
              : "p-8 sm:p-12 lg:grid-cols-[1.4fr_0.6fr] lg:items-center")
          }
        >
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <span className="border border-white/40 px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white">
                Campaign Live
              </span>
              <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-white/70">
                Season Colour: Teal
              </span>
            </div>

            <h2 className="mt-6 font-serif text-5xl leading-[1.05] tracking-tight text-white sm:text-6xl">
              Africa in Bloom.
            </h2>

            <p className="mt-5 max-w-xl text-base leading-relaxed text-white/85 sm:text-lg">
              This season belongs to teal: the colour of new growth, deep water
              and quiet confidence. It is showing up across wardrobes, artwork
              and album covers, and our Africa in Bloom campaign is running now
              to celebrate it.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/collab"
                className="inline-flex items-center bg-white px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.18em] transition hover:bg-black hover:text-white"
                style={{ color: BRAND.tealDeep }}
              >
                Join the campaign
                <ArrowRight className="ml-2 h-3 w-3" />
              </Link>

              <button
                onClick={() => {}}
                className="text-[11px] font-semibold uppercase tracking-[0.18em] text-white/80 underline-offset-4 hover:text-white hover:underline"
              >
                Why teal?
              </button>
            </div>
          </div>

          {/* Colour swatch strip */}
          {!compact && (
            <div className="hidden lg:block">
              <div className="grid grid-cols-3 gap-2">
                {[BRAND.tealDeep, BRAND.teal, BRAND.tealLight].map((c) => (
                  <div
                    key={c}
                    className="aspect-[3/4] border border-white/20"
                    style={{ backgroundColor: c }}
                  />
                ))}
              </div>
              <div className="mt-3 text-[10px] font-semibold uppercase tracking-[0.22em] text-white/70">
                The Bloom Palette
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function SiteHeader({ mode, setMode }) {
  const navBtn = (key, label) => (
    <button
      onClick={() => setMode(key)}
      className={
        "px-3 py-2 text-[11px] font-semibold tracking-[0.18em] uppercase transition " +
        (mode === key
          ? "bg-black text-white"
          : "bg-white text-black hover:bg-black/5")
      }
    >
      {label}
    </button>
  );

  return (
    <header className="sticky top-0 z-40 bg-white shadow-sm">
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="pt-4 sm:pt-5">
          <div className="flex items-center justify-between gap-3">
            <div className="hidden sm:flex items-center gap-4">
              <button
                onClick={() => setMode(MODES.culture)}
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-600 hover:text-black"
              >
                Culture X Music
              </button>

              <button
                onClick={() => setMode(MODES.fashion)}
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-600 hover:text-black"
              >
                Art X Fashion
              </button>

              <Link
                to="/echoes"
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-600 hover:text-black"
              >
                Echoes
              </Link>
            </div>

            <div className="flex items-center gap-4">
              <Link
                to="/ams"
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-600 hover:text-black"
              >
                AMS
              </Link>
              <Link
                to="/about"
                className="px-3 py-2 text-[11px] font-semibold tracking-[0.18em] uppercase transition bg-white text-black hover:bg-black/5"
              >
                About
              </Link>
              <Link
                to="/login"
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-600 hover:text-black"
              >
                Login
              </Link>
            </div>
          </div>

          <Rule className="mt-4" />

          <div className="grid grid-cols-1 items-center gap-3 py-4 sm:grid-cols-[1fr_auto_1fr]">
            <div className="hidden sm:block">
              <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">
                Africa Loud Suite
              </div>
              <div className="mt-1 text-xs text-neutral-600">
                Culture intelligence creative execution
              </div>
            </div>

            <button
              onClick={() => setMode(MODES.home)}
              className="mx-auto flex items-center gap-4 text-center"
              aria-label="Go to Home"
            >
              <img
                src={AfricaLoudIcon}
                alt="AL"
                className="w-10 h-10 object-contain sm:w-12 sm:h-12"
                onError={(e) => (e.target.style.display = "none")}
              />

              <div className="text-left">
                <div className="font-serif text-4xl tracking-tight text-neutral-950 sm:text-5xl leading-none">
                  Africa Loud
                </div>

                <div className="mt-1 text-[11px] font-semibold tracking-[0.25em] uppercase text-neutral-600">
                  Editorial
                </div>
              </div>
            </button>

            <div className="hidden justify-self-end sm:block text-right">
              <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-400">
                JHB / LAG / KGL
              </div>
            </div>
          </div>

          <Rule />

          <nav className="flex flex-wrap items-center justify-between gap-2 py-2">
            <div className="flex flex-wrap items-center gap-2">
              {navBtn(MODES.home, "Home")}
              {navBtn(MODES.culture, "Culture X Music")}
              {navBtn(MODES.fashion, "Art X Fashion")}

              <Link
                to="/film"
                className="px-3 py-2 text-[11px] font-semibold tracking-[0.18em] uppercase transition bg-white text-black hover:bg-black/5"
              >
                African Film
              </Link>

              <Link
                to="/echoes"
                className="px-3 py-2 text-[11px] font-semibold tracking-[0.18em] uppercase transition bg-white text-black hover:bg-black/5"
              >
                Echoes
              </Link>
            </div>

            <div className="hidden items-center gap-2 sm:flex">
              <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">
                Subscribe
              </div>
              <div className="h-3 w-px bg-black/15" />
            </div>
          </nav>

          <Rule />
        </div>
      </div>
    </header>
  );
}

function SiteFooter() {
  const links = [
    {
      label: "Instagram",
      href: "https://instagram.com/africaloudmag",
      external: true,
    },
    {
      label: "Twitter",
      href: "https://twitter.com/africaloudmag",
      external: true,
    },
    {
      label: "TikTok",
      href: "https://tiktok.com/@africaloudmag",
      external: true,
    },
    { label: "Terms", href: "/terms" },
    { label: "Privacy", href: "/terms" },
    { label: "Collab", href: "/collab" },
  ];

  return (
    <footer className="mt-12 bg-neutral-900 px-5 py-10">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-lg font-bold text-white font-serif">
            Africa Loud
          </div>

          <div className="flex flex-wrap gap-x-5 gap-y-2">
            {links.map((l) => (
              <a
                key={l.label}
                href={l.href}
                target={l.external ? "_blank" : undefined}
                rel={l.external ? "noopener noreferrer" : undefined}
                className="text-[11px] font-semibold tracking-[0.18em] uppercase text-white/70 hover:text-white transition"
              >
                {l.label}
              </a>
            ))}
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-white/10 text-[10px] tracking-widest uppercase text-white/40">
          &copy; 2026 Africa Loud. All rights reserved.
        </div>
      </div>
    </footer>
  );
}

function SponsorStrip() {
  return (
    <div className="mt-6">
      <Box className="p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">
              Partner
            </div>

            <div className="mt-1 font-serif text-xl tracking-tight text-neutral-950">
              ON THIRD - Premium Luxury
            </div>

            <div className="mt-1 text-sm text-neutral-700">
              Shop the scent with a 10% off offer via the partner link.
            </div>
          </div>

          <a
            href="https://onthird.co.za/"
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center justify-center border border-black px-5 py-3 text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-950 hover:bg-black hover:text-white transition"
            style={{ borderColor: BRAND.charcoal }}
          >
            Shop the scent 10% off
            <ArrowRight className="ml-2 h-3 w-3" />
          </a>
        </div>
      </Box>
    </div>
  );
}

function DirectorsPick() {
  const article = CULTURE_MUSIC_ARTICLES[0];

  return (
    <div className="mt-8">
      <div className="mb-4 flex items-center gap-3">
        <div className="h-2 w-2" style={{ backgroundColor: BRAND.burgundy }} />
        <span className="text-[11px] uppercase tracking-[0.22em] font-bold text-neutral-600">
          Featured Analysis
        </span>
      </div>

      <Link to={article.to}>
        <Box className="p-8 hover:shadow-xl transition-shadow duration-500">
          <TagPill tone="burgundy">Director's Pick</TagPill>

          <h3 className="mt-6 font-serif text-4xl leading-tight sm:text-5xl">
            {article.title}
          </h3>

          <p className="mt-5 max-w-2xl text-neutral-600 leading-relaxed text-lg">
            {article.blurb}
          </p>

          <div className="mt-8 inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest">
            Read full analysis
            <ArrowRight className="h-4 w-4" />
          </div>
        </Box>
      </Link>
    </div>
  );
}

function ArticleGrid({ articles, accent }) {
  return (
    <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
      {articles.map((article) => (
        <Link key={article.title} to={article.to} className="group">
          <Box className="p-6 h-full transition-all duration-300 group-hover:border-black/30 group-hover:shadow-lg">
            <TagPill tone={accent}>{article.tag}</TagPill>

            <div className="mt-4 font-serif text-2xl leading-snug tracking-tight text-neutral-950 group-hover:text-neutral-700 transition-colors">
              {article.title}
            </div>

            <p className="mt-3 text-sm text-neutral-600 leading-relaxed">
              {article.blurb}
            </p>
          </Box>
        </Link>
      ))}
    </div>
  );
}

function HomeTeaser({ go }) {
  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6">
      <div className="mt-6 grid gap-4 lg:grid-cols-[1.2fr_0.8fr]">
        <Box className="p-6 sm:p-10 flex flex-col justify-center bg-[#fafafa]">
          <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-neutral-500">
            Editorial
          </div>

          <h1 className="mt-6 font-serif text-5xl leading-[1.05] tracking-tight sm:text-7xl">
            Where African culture moves first.
          </h1>

          <p className="mt-6 max-w-xl text-neutral-600 leading-relaxed text-lg">
            Stories, signals and movements shaping music, fashion and urban
            identity across the continent.
          </p>

          <div className="mt-10">
            <button
              onClick={() => go(MODES.culture)}
              className="bg-black text-white px-8 py-4 text-[11px] font-semibold uppercase tracking-[0.2em] hover:bg-neutral-800 transition shadow-lg"
            >
              Enter Culture X Music
            </button>
          </div>
        </Box>

        <a
          href="mailto:contact@africaloudmag.com?subject=African%20Film%20Atlas%20Open%20Call&body=Hi%20I%27d%20like%20to%20know%20more"
          className="relative overflow-hidden min-h-[400px] block border border-black/15 hover:shadow-xl transition-all duration-300"
        >
          <img
            src="/African_Film_Atlas_Flyer.png"
            alt="African Film Atlas Open Call"
            className="h-full w-full absolute inset-0 object-cover transition-transform duration-700 hover:scale-105"
          />

          <div className="absolute bottom-0 left-0 right-0">
            <div className="bg-white/95 p-6 backdrop-blur-md border-t border-black/5">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-neutral-400">
                    Open Call
                  </div>

                  <div className="mt-1 text-lg font-semibold text-neutral-950 font-serif">
                    African Film Atlas
                  </div>
                </div>

                <ArrowRight className="h-5 w-5 text-neutral-400" />
              </div>
            </div>
          </div>
        </a>
      </div>

      <BloomAd />
      <SponsorStrip />
      <DirectorsPick />
    </div>
  );
}

export default function AfricaLoudHome() {
  const [mode, setMode] = useState(MODES.home);

  const renderPageContent = () => {
    switch (mode) {
      case MODES.home:
        return <HomeTeaser go={setMode} />;

      case MODES.culture:
        return (
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <div className="pt-10">
              <div className="flex items-center gap-3 mb-8">
                <div
                  className="h-2 w-2"
                  style={{ backgroundColor: BRAND.burgundy }}
                />
                <div className="text-[11px] font-bold tracking-[0.25em] uppearcase text-neutral-600">
                  Culture X Music
                </div>
              </div>

              <BloomAd compact />

              <ArticleGrid
                articles={CULTURE_MUSIC_ARTICLES}
                accent="burgundy"
              />
            </div>
          </div>
        );

      case MODES.fashion:
        return (
          <div className="mx-auto max-w-6xl px-4 sm:px-6">
            <div className="pt-10">
              <div className="flex items-center gap-3 mb-8">
                <div
                  className="h-2 w-2"
                  style={{ backgroundColor: BRAND.gold }}
                />
                <div className="text-[11px] font-bold tracking-[0.25em] uppercase text-neutral-600">
                  Art X Fashion
                </div>
              </div>

              <BloomAd compact />

              <ArticleGrid articles={ART_FASHION_ARTICLES} accent="gold" />
            </div>
          </div>
        );

      default:
        return <HomeTeaser go={setMode} />;
    }
  };

  return (
    <div className="bg-white text-neutral-950 min-h-screen">
      <Helmet>
        <title>{MODE_TITLES[mode] ?? "Africa Loud | Editorial"}</title>
      </Helmet>

      <style>
        {`
          @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,400;700;800&family=Outfit:wght@300;400;600;700&display=swap');

          body, div, p, span, button, a {
            font-family: 'Outfit', sans-serif;
          }

          .font-serif, h1, h2, h3, h4 {
            font-family: 'Fraunces', serif !important;
            font-weight: 700;
          }
        `}
      </style>

      <SeasonBanner />
      <SiteHeader mode={mode} setMode={setMode} />

      <AnimatePresence mode="wait">
        <motion.div
          key={mode}
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -15 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        >
          {renderPageContent()}
        </motion.div>
      </AnimatePresence>

      <SiteFooter />
    </div>
  );
}